import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { PromptTypes } from '@kinde/js-utils';
import { CompanyAuthMethodsCard } from '@/features/authentication/components/CompanyAuthMethodsCard';
import { discoverAuth, type AuthDiscoverResponse } from '@/features/authentication/api/authApi';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useAutoLoginFromQuery } from '@/features/authentication/hooks/useAutoLoginFromQuery';
import { logger } from '@/shared/observability/frontendLogger';
import { trackRumAction } from '@/shared/observability/rum';
import { hashForObservability } from '@/shared/observability/redaction';
import LoginImage from '@/assets/login_background.png';
import { HomeEmailDiscoveryStep } from './components/HomeEmailDiscoveryStep';
import { PublicAuthHeader } from './components/PublicAuthHeader';
import { getDashboardPath } from './homeAuthHelper';

const EMAIL_CONNECTION_ID = import.meta.env.VITE_KINDE_EMAIL_CONNECTION_ID;
const AUTH_INTENT_KEY = 'bquest_auth_intent';

const HomePage = () => {
  const [email, setEmail] = useState('');
  const [authError, setAuthError] = useState('');
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [loginDebounce, setLoginDebounce] = useState(false);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveryResult, setDiscoveryResult] = useState<AuthDiscoverResponse | null>(null);
  const [resolvedEmail, setResolvedEmail] = useState<string | null>(null);
  const { login: kindeLogin, isLoading: kindeLoading } = useKindeAuth();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { companySlug: routeCompanySlug } = useParams<{ companySlug: string }>();
  const hasNavigatedRef = useRef(false);

  // Auto-login flow now lives in a shared hook so PreLoginLanding (new home)
  // handles the same query-param handoff after legacy Management API signups.
  useAutoLoginFromQuery();

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const emailParam = query.get('email');
    const errorParam = query.get('error');

    if (emailParam) {
      setEmail(emailParam);
      setShowPlaceholder(false);
    }

    if (errorParam) {
      setAuthError(decodeURIComponent(errorParam));
    }
  }, [location.search]);

  useEffect(() => {
    if (user && !hasNavigatedRef.current) {
      navigate(getDashboardPath(user.role));
      hasNavigatedRef.current = true;
    }
  }, [navigate, user]);

  const resetResolvedState = () => {
    setDiscoveryResult(null);
    setResolvedEmail(null);
  };

  const triggerOtpLogin = async (
    targetEmail: string,
    nextStep: AuthDiscoverResponse | null,
  ) => {
    if (loginDebounce || kindeLoading) return;
    setLoginDebounce(true);

    let emailHash: string | undefined;
    try {
      emailHash = await hashForObservability(targetEmail);
      trackRumAction('login_attempt', { emailHash, method: 'otp' });
      logger.info('OTP login attempt', { emailHash });
    } catch {
      trackRumAction('login_attempt', { method: 'otp' });
      logger.warn('OTP login attempt (hashing unavailable)');
    }

    try {
      setAuthError('');
      sessionStorage.setItem(
        AUTH_INTENT_KEY,
        JSON.stringify({
          method: 'otp',
          email: targetEmail,
          companySlug: nextStep?.companySlug ?? routeCompanySlug ?? null,
          expectedCompanySlug: nextStep?.companySlug ?? routeCompanySlug ?? null,
        }),
      );

      await kindeLogin({
        connectionId: EMAIL_CONNECTION_ID,
        loginHint: targetEmail,
        prompt: PromptTypes.login,
      });
    } catch (error) {
      setAuthError('Authentication failed. Please try again.');
      logger.error('OTP login failed', {
        emailHash,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setLoginDebounce(false);
    }
  };

  const runDiscovery = async (targetEmail: string) => {
    const normalizedEmail = targetEmail.trim().toLowerCase();
    setIsDiscovering(true);

    try {
      setAuthError('');
      const result = await discoverAuth({
        email: normalizedEmail,
        ...(routeCompanySlug ? { companySlug: routeCompanySlug } : {}),
      });
      setDiscoveryResult(result);
      setResolvedEmail(normalizedEmail);
      return result;
    } catch (error) {
      setAuthError(
        error instanceof Error ? error.message : 'Unable to determine your login options.',
      );
      return null;
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) {
      setAuthError('Email address is required.');
      return;
    }

    if (!discoveryResult || resolvedEmail !== normalizedEmail) {
      await runDiscovery(normalizedEmail);
      return;
    }

    if (discoveryResult.allowOtp) {
      await triggerOtpLogin(normalizedEmail, discoveryResult);
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setAuthError('');
    resetResolvedState();
  };

  const handleChangeEmailStep = () => {
    setAuthError('');
    resetResolvedState();
  };

  const showOtpAction = Boolean(discoveryResult?.allowOtp);
  const showSsoAction = Boolean(discoveryResult?.authMethods.includes('sso'));
  const isSubmittingLogin = loginDebounce || kindeLoading;
  const isEmailStep = !discoveryResult;
  const normalizedEmail = email.trim().toLowerCase();

  return (
    <div className="min-h-screen bg-background">
      <div className="min-h-screen bg-[#026857]">
        <PublicAuthHeader />

        <main className="md:container flex min-h-[calc(100svh-72px)] items-center gap-10 px-4 py-6 max-md:flex-col md:gap-12 md:px-6">
          <section className="w-full max-w-[32rem] self-center py-2">
            <h1 className="font-recoleta text-4xl text-white">Welcome Back!</h1>
            <div className="py-6">
              {isEmailStep ? (
                <HomeEmailDiscoveryStep
                  animationKey="home-email-step"
                  email={email}
                  showPlaceholder={showPlaceholder}
                  isDiscovering={isDiscovering}
                  authError={authError}
                  onSubmit={handleSubmit}
                  onEmailFocus={() => setShowPlaceholder(false)}
                  onEmailBlur={() => setShowPlaceholder(true)}
                  onEmailChange={handleEmailChange}
                />
              ) : (
                <CompanyAuthMethodsCard
                  animationKey={`home-auth-${discoveryResult?.companySlug ?? 'generic'}-${
                    discoveryResult?.authMethods.join('-') ?? 'otp'
                  }`}
                  companyName={discoveryResult?.companyName}
                  companyLogoUrl={discoveryResult?.companyLogoUrl}
                  email={resolvedEmail ?? normalizedEmail}
                  emailMode="summary"
                  onChangeEmail={handleChangeEmailStep}
                  showOtpAction={showOtpAction}
                  otpButtonLabel="Send Email Code"
                  onOtpAction={() => {
                    void triggerOtpLogin(normalizedEmail, discoveryResult);
                  }}
                  showSsoAction={showSsoAction}
                  companySlug={discoveryResult?.companySlug}
                  connectionId={discoveryResult?.kindeConnectionId}
                  orgCode={discoveryResult?.kindeOrgId}
                  isBusy={isSubmittingLogin}
                  busyLabel="Connecting..."
                  authError={authError || null}
                  onError={(message) => setAuthError(message ?? '')}
                />
              )}
            </div>
          </section>

          <section className="w-full max-w-[30rem] self-center rounded-2xl p-1 md:max-w-[34rem]">
            <img
              src={LoginImage}
              alt="signup_background"
              className="max-h-[32rem] w-full rounded-2xl bg-cover bg-center object-cover"
            />
          </section>
        </main>
      </div>
    </div>
  );
};

export default HomePage;
